import React, { useEffect, useState, useMemo } from 'react';
import { Grid2, Typography, IconButton, Tooltip, Button, CircularProgress } from '@mui/material';
import { useGetBookingItems, getBookingItems } from '@openapi/booking-items/booking-items';
import { useGetTimeslot } from '@openapi/timeslots/timeslots';
import { useGetTicketTypes } from '@openapi/ticket-types/ticket-types';
import useNotify from '../../hooks/useNotify';
import useSorting from '../../hooks/useSorting';
import PageWithTitle from '../../components/atom/PageWithTitle';
import CateringViewTable from './components/CateringViewTable';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import FilterComponent from './components/FilterComponent';
import SeatViewer from './components/SeatViewer';
import NoteFilterComponent from '../../components/atom/NoteFilterComponent';
import CommonButton from '../../components/atom/CommonButton';
import { formatDateTimeInVenueTz } from '../../helpers/formatDateTime';
import ClockIcon from '@mui/icons-material/AccessTime';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { collectAllData, exportToXlsx } from '../../helpers/excelExportHelpers';

const CateringViewPage = () => {
    const { t } = useTranslation();
    const { notifyError, notifySuccess } = useNotify();
    const navigate = useNavigate();
    const { timeslotId } = useParams();
    const { sort, sortDir, setSorting } = useSorting(['ticketSeatName', 'ASC']);
    const [ticketTypeFilter, setTicketTypeFilter] = useState(0);
    const [noteStateFilter, setNoteStateFilter] = useState(0);
    const [checkinNoteStateFilter, setCheckinNoteStateFilter] = useState(0);
    const [showId, setShowId] = useState(null);
    const [timeslotInfo, setTimeslotInfo] = useState(null);

    const {
        data,
        isLoading: loading,
        error,
    } = useGetBookingItems(
        {
            timeslotId: timeslotId,
            ticketTypeId: ticketTypeFilter || undefined,
            noteState: noteStateFilter === 0 ? undefined : noteStateFilter,
            checkinNoteState: checkinNoteStateFilter === 0 ? undefined : checkinNoteStateFilter,
            page: 0,
            pageSize: 10000, // for now, we set a high page size to get all items
            sort,
            sortDir,
            onlyHaveSeat: true, // only show booking items with seats
        },
        {
            query: {
                enabled: !!timeslotId,
                onError: notifyError,
            },
        },
    );

    // Load timeslot information for display
    const { data: timeslotData } = useGetTimeslot(Number(timeslotId), {
        query: {
            enabled: !!timeslotId,
            onError: notifyError,
        },
    });

    // Load ticket types for the show to get colors
    const { data: ticketTypesData } = useGetTicketTypes(
        {
            optionalId: Number(showId),
            page: 0,
            pageSize: 1000,
            onlyShowInList: true,
        },
        {
            query: {
                enabled: !!showId,
                onError: notifyError,
            },
        },
    );

    // Create a mapping of ticket type ID to color
    const ticketTypeColorMap = useMemo(() => {
        if (!ticketTypesData?.ticketTypes) return {};
        const map = {};
        ticketTypesData.ticketTypes.forEach((tt) => {
            map[tt.id] = tt.color;
        });
        return map;
    }, [ticketTypesData]);

    // seat viewer
    const [elements, setElements] = useState([]);
    const [selectedElementId, setSelectedElementId] = useState(null);

    const [generatingXlsx, setGeneratingXlsx] = useState(false);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        if (error) {
            notifyError(error);
        }
    }, [error, notifyError]);

    useEffect(() => {
        if (timeslotData) {
            setTimeslotInfo(timeslotData);
            setShowId(timeslotData.showId);
        }
    }, [timeslotData]);

    // reset selected element when any filter changes
    useEffect(() => {
        setSelectedElementId(null);
    }, [timeslotId, ticketTypeFilter, noteStateFilter, checkinNoteStateFilter]);

    const handleClear = () => {
        setTicketTypeFilter(0);
        setNoteStateFilter(0);
        setCheckinNoteStateFilter(0);
    };

    const handleChangeTimeslot = () => {
        navigate('/cateringview');
    };

    return (
        <PageWithTitle
            title={`${t('bookingitems.cateringview')}`}
            headline={[
                timeslotInfo && (
                    <Grid2
                        key={'timeslot-info'}
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        size={{ xs: 12 }}
                    >
                        <Grid2>
                            <Typography variant="body1">
                                {formatDateTimeInVenueTz(timeslotInfo.startTime)}
                                <br />
                                <small>{timeslotInfo.showTitle}</small>
                            </Typography>
                        </Grid2>
                        <Grid2>
                            <Tooltip title={t('timeslots.changeTimeslot')}>
                                <IconButton onClick={handleChangeTimeslot}>
                                    <ClockIcon />
                                </IconButton>
                            </Tooltip>
                        </Grid2>
                    </Grid2>
                ),
            ]}
        >
            <Grid2
                container
                size={{ xs: 12 }}
                spacing={1}
                direction="row"
                alignItems="flex-end"
                justifyContent="flex-end"
            >
                <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <FilterComponent
                        filterValue={ticketTypeFilter}
                        setFilterValue={setTicketTypeFilter}
                        showId={showId}
                        itemName="ticketTypes"
                        title="tickettypes.title"
                        primaryName="name"
                    />
                </Grid2>
                <Grid2 size={{ xs: 6, md: 2 }}>
                    <NoteFilterComponent
                        filterValue={noteStateFilter}
                        setFilterValue={setNoteStateFilter}
                        title={t('bookingitems.columns.note')}
                    />
                </Grid2>
                <Grid2 size={{ xs: 6, md: 2 }}>
                    <NoteFilterComponent
                        filterValue={checkinNoteStateFilter}
                        setFilterValue={setCheckinNoteStateFilter}
                        title={t('bookingitems.columns.checkinNote')}
                    />
                </Grid2>
                <Grid2 size={{ xs: 6, md: 3 }}>
                    <CommonButton
                        onClick={handleClear}
                        fullWidth
                        label={t('common.clear')}
                        buttonType="text"
                    />
                </Grid2>
                <Grid2 size={{ xs: 6, md: 1 }}>
                    <Tooltip title="Export">
                        <Button
                            fullWidth
                            disabled={generatingXlsx}
                            onClick={async () => {
                                setGeneratingXlsx(true);
                                let listToExport = [];
                                try {
                                    listToExport = await collectAllData({
                                        data,
                                        apiFunction: (page, pageSize) => {
                                            return getBookingItems({
                                                timeslotId: timeslotId,
                                                ticketTypeId: ticketTypeFilter || undefined,
                                                noteState:
                                                    noteStateFilter === 0
                                                        ? undefined
                                                        : noteStateFilter,
                                                checkinNoteState:
                                                    checkinNoteStateFilter === 0
                                                        ? undefined
                                                        : checkinNoteStateFilter,
                                                onlyHaveSeat: true,
                                                page,
                                                pageSize,
                                            });
                                        },
                                        setProgress,
                                        responseData: 'bookingItems',
                                        mappingName: 'cateringview',
                                    });
                                    exportToXlsx(listToExport, 'cateringview');
                                    notifySuccess(t('bookingitems.notifications.exportSuccess'));
                                } catch (err) {
                                    notifyError(err);
                                }
                                setGeneratingXlsx(false);
                                setProgress(0);
                            }}
                            label={t('common.export')}
                        >
                            {generatingXlsx ? (
                                <CircularProgress
                                    variant="determinate"
                                    color="inherit"
                                    size={25}
                                    value={progress}
                                />
                            ) : (
                                <FileDownloadIcon />
                            )}
                        </Button>
                    </Tooltip>
                </Grid2>
            </Grid2>
            {timeslotId && showId ? (
                <Grid2 size={{ xs: 12 }} sx={{ mt: 2 }}>
                    <SeatViewer
                        elements={elements}
                        setElements={setElements}
                        selectedElementId={selectedElementId}
                        setSelectedElementId={setSelectedElementId}
                        showId={showId}
                        timeslotId={timeslotId}
                        data={data?.bookingItems}
                        ticketTypeColorMap={ticketTypeColorMap}
                    />
                </Grid2>
            ) : null}
            <CateringViewTable
                rows={data?.bookingItems}
                total={data?.total}
                loading={loading}
                sort={sort}
                sortDir={sortDir}
                setSorting={setSorting}
            />
        </PageWithTitle>
    );
};

export default CateringViewPage;
